import { useState, useEffect } from 'react' // 追加
import { ArrowLeft, Star, LineChart as ChartIcon } from 'lucide-react' // 修正
import clsx from 'clsx'
import { useStore, selectFilteredSymbols, selectCurrentPrediction, selectCurrentOHLCV } from '../../store/useStore'
import SymbolCard from './SymbolCard'
import CandlestickChart from '../charts/CandlestickChart'
import PredictionChart from '../charts/PredictionChart'
import MultiTimeframeSummary from './MultiTimeframeSummary'
import { getSymbolName } from '../../types'

export default function Dashboard() {
  const {
    selectedSymbol,
    setSelectedSymbol,
    predictions,
    loadingPredictions,
    ensemblePredictions, // 追加
    loadingEnsemble,    // 追加
    loadEnsemblePrediction, // 追加
    metadata,
    selectedInterval,
  } = useStore()

  const [viewMode, setViewMode] = useState<'standard' | 'ensemble'>('standard') // 追加

  useEffect(() => {
    if (selectedSymbol && viewMode === 'ensemble' && !ensemblePredictions[selectedSymbol.toUpperCase()]) {
      loadEnsemblePrediction(selectedSymbol)
    }
  }, [selectedSymbol, viewMode, ensemblePredictions, loadEnsemblePrediction])

  const filteredSymbols = useStore(selectFilteredSymbols)
  const currentPrediction = useStore(selectCurrentPrediction)
  const currentOHLCV = useStore(selectCurrentOHLCV)

  // ── Symbol detail view ────────────────────────────────────────────────────

  if (selectedSymbol) {
    const key = `${selectedSymbol}_${selectedInterval}`
    const symbolPrediction = predictions[key]
    const ohlcvData = currentOHLCV.length > 0
      ? currentOHLCV
      : symbolPrediction?.ohlcv ?? []

    return (
      <div className="flex flex-col gap-4 max-w-full">
        {/* Back button */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSelectedSymbol('')}
            className="btn-ghost flex items-center gap-1.5 text-trading-muted"
          >
            <ArrowLeft size={14} />
            銘柄一覧
          </button>
          <span className="text-trading-border">·</span>
          <span className="text-sm text-trading-text font-mono font-semibold">
            {selectedSymbol} {(metadata[selectedSymbol.toUpperCase()] || getSymbolName(selectedSymbol)) && ` · ${metadata[selectedSymbol.toUpperCase()] || getSymbolName(selectedSymbol)}`}
          </span>
        </div>

        {/* Expanded symbol card */}
        <SymbolCard symbol={selectedSymbol} expanded={true} />

        {/* Candlestick chart with prediction overlay */}
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-wider text-trading-muted mb-2 px-1">
            価格チャート＋AI予測
          </h2>
          <CandlestickChart
            ohlcv={ohlcvData}
            prediction={currentPrediction?.prediction ?? null}
            height={420}
            interval={selectedInterval}
          />
        </div>

        {/* Multi-timeframe 1-day comparison */}
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-wider text-trading-muted mb-2 px-1">
            マルチタイムフレーム予測
          </h2>
          <MultiTimeframeSummary
            symbol={selectedSymbol}
            predictions={predictions}
            loadingPredictions={loadingPredictions}
          />
        </div>

        {/* Prediction details for selected interval */}
        <div>
          <div className="flex items-center justify-between mb-2 px-1">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-trading-muted">
              AI予測詳細
            </h2>
            
            {/* NEW MODE SWITCHER */}
            <div className="flex p-0.5 bg-trading-panel border border-trading-border rounded-lg">
              <button
                onClick={() => setViewMode('standard')}
                className={clsx(
                  "px-3 py-1 text-[10px] font-bold rounded-md flex items-center gap-1.5 transition-all",
                  viewMode === 'standard' 
                    ? "bg-trading-blue/20 text-trading-blue shadow-sm" 
                    : "text-trading-muted hover:text-trading-text"
                )}
              >
                <ChartIcon size={11} />
                {selectedInterval === '1h' ? '1H足' : selectedInterval === '4h' ? '4H足' : '1D足'}
              </button>
              <button
                onClick={() => setViewMode('ensemble')}
                className={clsx(
                  "px-3 py-1 text-[10px] font-bold rounded-md flex items-center gap-1.5 transition-all relative overflow-hidden",
                  viewMode === 'ensemble' 
                    ? "bg-cyan-500/20 text-cyan-400 shadow-[0_0_10px_rgba(6,182,212,0.2)]" 
                    : "text-trading-muted hover:text-trading-text"
                )}
              >
                <Star size={11} className={clsx(viewMode === 'ensemble' && "fill-cyan-400")} />
                🌟 統合アンサンブル
              </button>
            </div>
          </div>

          {viewMode === 'ensemble' ? (
            loadingEnsemble[selectedSymbol.toUpperCase()] && !ensemblePredictions[selectedSymbol.toUpperCase()] ? (
              <div className="trading-card flex items-center justify-center py-16">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 rounded-full border-2 border-cyan-400 border-t-transparent animate-spin" />
                  <p className="text-sm text-cyan-400 font-medium">マルチタイムフレーム統合中...</p>
                </div>
              </div>
            ) : ensemblePredictions[selectedSymbol.toUpperCase()] ? (
              <PredictionChart
                prediction={currentPrediction?.prediction || {} as any} 
                currentPrice={ensemblePredictions[selectedSymbol.toUpperCase()].current_price}
                symbol={selectedSymbol}
                ensemble={ensemblePredictions[selectedSymbol.toUpperCase()].ensemble}
              />
            ) : null
          ) : (
            currentPrediction ? (
              <PredictionChart
                prediction={currentPrediction.prediction}
                currentPrice={currentPrediction.current_price}
                symbol={selectedSymbol}
              />
            ) : (
              <div className="trading-card flex items-center justify-center py-16">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 rounded-full border-2 border-trading-blue border-t-transparent animate-spin" />
                  <p className="text-sm text-trading-muted">AI予測を読み込み中...</p>
                </div>
              </div>
            )
          )}
        </div>

      </div>
    )
  }

  // ── Grid view ─────────────────────────────────────────────────────────────

  if (filteredSymbols.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-trading-blue border-t-transparent animate-spin" />
          <p className="text-sm text-trading-muted">市場データを読み込み中...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold text-trading-text">
          マーケット概況
          <span className="ml-2 text-xs text-trading-muted font-normal">
            {filteredSymbols.length} 銘柄
          </span>
        </h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
        {filteredSymbols.map((symbol) => (
          <SymbolCard
            key={symbol}
            symbol={symbol}
            expanded={false}
            onClick={() => setSelectedSymbol(symbol)}
          />
        ))}
      </div>
    </div>
  )
}
